import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms'

import { distinctUntilChanged, map } from 'rxjs'

import { FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxIconModule, FlxValueSliderModule } from '@flowx/angular-ui-toolkit'
import {
  CurrencyObject,
  DataModelType,
  LabelAccessibilityProps,
  SliderTemplateConfig,
  getLocalizedCurrency,
  isFormattingEnabled,
  isOfDataType,
  processDataValueFromPath$,
  prototypeInstanceMetaToPath,
} from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import {
  getComputedValueSignal,
  getDynamicPropSignal,
  getErrorMessages,
  getThemeIcon,
} from '../helpers'

@Component({
  selector: 'flx-slider-wrapper',
  imports: [
    CommonModule,
    FlxValueSliderModule,
    FlxLocalizePipe,
    ReactiveFormsModule,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  templateUrl: './slider-wrapper.component.html',
  styleUrl: './slider-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxSliderWrapperComponent implements OnInit {
  #injector = inject(Injector)
  templateConfig = input.required<SliderTemplateConfig>()
  control = input.required<FormControl>()
  errors = input<ValidationErrors | null>(null)
  showErrors = input<boolean>(false)
  disabledState = input<boolean>(false)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)

  icons = computed(() => ({
    helper: getThemeIcon(ThemeComponent.HelperTooltip, 'helperIcon'),
  }))

  // dynamic props
  label: Signal<string>
  helper: Signal<string>
  prefix: Signal<string>
  suffix: Signal<string>
  minValue: Signal<string>
  maxValue: Signal<string>
  step: Signal<string>
  currencyCode: Signal<string>
  errorMessages: Record<string, Signal<string>>
  ariaLabel: Signal<string>

  displayFormatFn = computed(() =>
    isOfDataType(DataModelType.CURRENCY, this.templateConfig()) ||
    isFormattingEnabled(this.templateConfig())
      ? getLocalizedCurrency(this.templateConfig(), this.currencyCode())
      : null
  )

  readonly elementUniqueId = `${crypto.randomUUID()}`
  readonly labelId = `${this.elementUniqueId}-form-item-label`
  readonly helperId = `${this.elementUniqueId}-helper`
  readonly errorId = `${this.elementUniqueId}-error`

  readonly describedByIds = computed(() => {
    const ids: string[] = []
    const helper = this.helper()
    const showErrors = this.showErrors()
    const errors = this.errors()

    if (showErrors && errors) {
      Object.keys(errors).forEach((_, index) => {
        ids.push(`${this.errorId}${index}`)
      })
    } else if (helper) {
      ids.push(this.helperId)
    }
    return ids.join(' ')
  })

  ngOnInit(): void {
    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const config = this.templateConfig()
      const {
        label,
        helper,
        prefix,
        suffix,
        minValue,
        maxValue,
        isComputedMinValue,
        isComputedMaxValue,
        step,
      } = this.flowxProps()
      this.label = getDynamicPropSignal(label, this.templateConfig())
      this.helper = getDynamicPropSignal(helper, this.templateConfig())
      this.prefix = getDynamicPropSignal(prefix, this.templateConfig())
      this.suffix =
        isOfDataType(DataModelType.CURRENCY, this.templateConfig()) ||
        isFormattingEnabled(this.templateConfig())
          ? signal(undefined)
          : getDynamicPropSignal(suffix, this.templateConfig())
      this.step = getDynamicPropSignal(step as any, this.templateConfig())
      this.minValue = isComputedMinValue
        ? getComputedValueSignal(minValue as any, this.templateConfig())
        : getDynamicPropSignal(minValue as any, this.templateConfig())
      this.maxValue = isComputedMaxValue
        ? getComputedValueSignal(maxValue as any, this.templateConfig())
        : getDynamicPropSignal(maxValue as any, this.templateConfig())
      this.ariaLabel = getDynamicPropSignal(this.accessibility()?.label, config)

      const { key, prototypeInstanceMeta } = config
      const fieldKey = prototypeInstanceMeta
        ? `${prototypeInstanceMetaToPath(prototypeInstanceMeta)}.${key}`
        : key
      this.currencyCode = toSignal(
        processDataValueFromPath$(config.processInstanceUuid, fieldKey).pipe(
          map((value) => (value as CurrencyObject)?.code ?? undefined),
          distinctUntilChanged()
        )
      )

      this.errorMessages = getErrorMessages(config)
    })
  }
}
